import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import shipmentsPostProcessor from '../helpers/shipmentsPostProcessor';
import { IModifiedShipment } from '../models/shipment';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';

/**
 * Hook to fetch a single shipment by ID, with all its relations
 * (returnShipment, originalShipment).
 * Uses react-query's cache for deduplication.
 *
 * @returns A callable `fetchShipment` function
 */
export const useGetShipmentById = () => {
  const [, makeRequest] = useImperativeRequestWrapper('shipment');
  const queryClient = useQueryClient();

  const fetchShipment = useCallback(
    async (shipmentId: string): Promise<IModifiedShipment | null> => {
      try {
        const data = await queryClient.fetchQuery({
          queryKey: ['shipment', shipmentId],
          queryFn: async () => {
            const res = await makeRequest({
              path: `/shipments/${shipmentId}`,
              method: 'get',
            });

            if (res.error || !res.data) {
              throw new Error(res.error?.message || 'Failed to fetch shipment');
            }

            if (!res.data.shipment) {
              throw new Error('Shipment not found');
            }

            const processedData = shipmentsPostProcessor({
              items: [res.data.shipment],
            });

            return processedData.items[0] as IModifiedShipment;
          },
          staleTime: 0,
        });

        return data;
      } catch (error) {
        console.error('[useGetShipmentById] Error fetching shipment:', error);

        return null;
      }
    },
    [makeRequest, queryClient]
  );

  return { fetchShipment };
};
